import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { useErrorNotification } from '@providers/ErrorsNotificationProvider';

import * as accountApi from '@features/shared/api/account/clients';
import * as clubApi from '@features/shared/api/club/clients';
import { components } from '@features/shared/api/club/clients';
import { ClockControlType } from '@features/shared/api/typings';

export function usePlayerEloStatsQuery({
  enabled = true,
  userId,
  staleTime,
}: {
  enabled?: boolean;
  userId?: string;
  staleTime?: number;
}) {
  return useQuery({
    enabled: enabled && !!userId,
    staleTime,
    queryKey: ['/api/v1/account/users/{userId}/elo', userId],
    queryFn: async () =>
      (
        await accountApi.GET('/api/v1/account/users/{userId}/elo', {
          params: {
            path: {
              userId: userId!,
            },
          },
        })
      ).data!,
  });
}

export function usePlayerGameStatsQuery({
  enabled = true,
  userId,
  staleTime,
}: { enabled?: boolean; userId?: string | null; staleTime?: number } = {}) {
  return useQuery({
    enabled: enabled && !!userId,
    staleTime,
    queryKey: ['/api/v1/account/users/{userId}/stats', userId],
    queryFn: async () =>
      (
        await accountApi.GET('/api/v1/account/users/{userId}/stats', {
          params: {
            path: {
              userId: userId!,
            },
          },
        })
      ).data!,
  });
}

export function usePlayerClubsAndTournamentsQuery({
  enabled = true,
  staleTime,
}: { enabled?: boolean; staleTime?: number } = {}) {
  return useQuery({
    enabled,
    staleTime,
    queryKey: ['/api/v1/user/account/clubs-and-tournaments'],
    queryFn: async () => (await accountApi.GET('/api/v1/user/account/clubs-and-tournaments')).data!,
  });
}

export function usePlayerTournamentsQuery({
  enabled = true,
  userId,
  upcoming,
  limit,
  offset,
}: {
  enabled?: boolean;
  userId?: string;
  upcoming: boolean;
  limit: number;
  offset?: number;
}) {
  const result = useInfiniteQuery({
    enabled: enabled && !!userId,
    placeholderData: (prev) => prev,
    getNextPageParam: (lastPage: {
      nextCursor?: number | null;
      data: accountApi.components['schemas']['UserTournamentsDto'];
    }) => lastPage.nextCursor,
    initialPageParam: 0,
    queryKey: ['/api/v1/account/users/{userId}/tournaments', userId, upcoming, limit, offset],
    queryFn: async ({ pageParam }) => {
      const res = await accountApi.GET('/api/v1/account/users/{userId}/tournaments', {
        params: {
          path: {
            userId: userId!,
          },
          query: {
            upcoming,
            limit,
            offset: pageParam * limit,
          },
        },
      });

      return {
        nextCursor:
          res.data?.myTournaments && res.data.myTournaments.length >= limit
            ? pageParam + 1
            : undefined,
        data: res.data!,
      };
    },
  });

  return {
    ...result,
    data: {
      myTournaments: result?.data?.pages?.reduce(
        (
          res: accountApi.components['schemas']['TournamentShortDto'][],
          p: { data: accountApi.components['schemas']['UserTournamentsDto'] },
        ) => res.concat(p.data.myTournaments),
        [],
      ) as accountApi.components['schemas']['TournamentShortDto'][],
      myTournamentsTotalCount: result?.data?.pages[0]?.data.myTournamentsTotalCount,
    },
  };
}

export function usePlayerEloHistoryQuery({
  enabled = true,
  userId,
  periodType,
  periodsCount,
  clockControlType,
}: {
  enabled?: boolean;
  userId?: string;
  periodType: accountApi.components['schemas']['EloHistoryPeriodType'];
  periodsCount: number;
  clockControlType: ClockControlType;
}) {
  return useQuery({
    enabled: enabled && !!userId,
    placeholderData: (prev) => prev,
    queryKey: [
      '/api/v1/account/users/{userId}/elo-history',
      userId,
      periodType,
      periodsCount,
      clockControlType,
    ],
    queryFn: async () =>
      (
        await accountApi.GET('/api/v1/account/users/{userId}/elo-history', {
          params: {
            path: {
              userId: userId!,
            },
            query: {
              periodType,
              periodsCount,
              clockControlType,
            },
          },
        })
      ).data!,
  });
}

export function usePlayerGamesHistoryQuery({
  enabled = true,
  userId,
  clockControlType,
  start,
  end,
  limit,
  offset,
}: {
  enabled?: boolean;
  userId?: string;
  clockControlType?: ClockControlType;
  start?: Date | null;
  end?: Date | null;
  limit: number;
  offset?: number;
}) {
  const result = useInfiniteQuery({
    enabled: enabled && !!userId,
    placeholderData: (prev) => prev,
    getNextPageParam: (lastPage: { nextCursor?: number | null }) => lastPage.nextCursor,
    initialPageParam: 0,
    queryKey: [
      '/api/v1/account/users/{userId}/games-history',
      userId,
      clockControlType,
      start,
      end,
      limit,
      offset,
    ],
    queryFn: async ({ pageParam }) => {
      const result = await accountApi.GET('/api/v1/account/users/{userId}/games-history', {
        params: {
          path: {
            userId: userId!,
          },
          query: {
            clockControlType,
            fromTimestampMs: start?.getTime(),
            toTimestampMs: end?.getTime(),
            limit,
            offset: pageParam * limit,
          },
        },
      });
      return {
        nextCursor: result.data && result.data.length >= limit ? pageParam + 1 : undefined,
        data: result.data,
      };
    },
  });

  return {
    ...result,
    data: result?.data?.pages?.reduce(
      // @ts-expect-error TS2769
      (
        res: accountApi.components['schemas']['UserGameHistoryItemDto'][],
        p: {
          data: accountApi.components['schemas']['UserGameHistoryItemDto'][];
        },
      ) => res.concat(p.data),
      [],
    ) as accountApi.components['schemas']['UserGameHistoryItemDto'][],
  };
}

export function usePlayerGamesStatsQuery({
  enabled = true,
  userId,
  clockControlType,
  start,
  end,
}: {
  enabled?: boolean;
  userId?: string;
  clockControlType: ClockControlType;
  start?: Date | null;
  end?: Date | null;
}) {
  return useQuery({
    enabled: enabled && !!userId,
    placeholderData: (prev) => prev,
    queryKey: [
      '/api/v1/account/users/{userId}/games-statistics',
      userId,
      clockControlType,
      start,
      end,
    ],
    queryFn: async () => {
      const result = await accountApi.GET('/api/v1/account/users/{userId}/games-statistics', {
        params: {
          path: {
            userId: userId!,
          },
          query: {
            clockControlType,
            fromTimestampMs: start?.getTime(),
            toTimestampMs: end?.getTime(),
          },
        },
      });
      return result.data;
    },
  });
}

export function useRcfAttributesMutation() {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({
      items,
    }: {
      items: clubApi.components['schemas']['SetUsersRcfIdRequestItem'][];
    }) => {
      return (
        await clubApi.POST(`/api/v1/admin/rcf-ratings/users`, {
          body: {
            items,
          },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useRcfUsersQuery({ query, limit }: { query: string; limit: number }) {
  const result = useInfiniteQuery({
    enabled: !!query,
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: Array<components['schemas']['RcfRatingDto']> | undefined,
      _,
      lastPageParam,
    ) => {
      return !lastPage || lastPage?.length >= limit ? lastPageParam + 1 : undefined;
    },
    placeholderData: (prev) => prev,
    queryKey: ['/api/v1/admin/rcf-ratings/suggest', query],
    queryFn: async ({ pageParam }) => {
      return (
        await clubApi.GET('/api/v1/admin/rcf-ratings/suggest', {
          params: {
            query: {
              query,
              limit,
              offset: pageParam * limit,
            },
          },
        })
      ).data;
    },
  });

  let data: components['schemas']['RcfRatingDto'][] = [];
  result?.data?.pages?.forEach((page) => {
    data = data.concat(page!);
  });

  return { ...result, data };
}

export function useCertificateTemplateQuery(url?: string) {
  return useQuery({
    enabled: !!url,
    queryKey: ['certificate-template', url],
    queryFn: async () => {
      const response = await fetch(url!, {
        cache: 'force-cache',
      });
      if (!response.ok) throw new Error('Failed to load svg template for certificate');
      return response.text();
    },
    staleTime: Infinity, // Mark as stale never (until cacheTime expires)
  });
}
